// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@guestsupply/App/Components/Cart/CartLineContainer";
import { getNotifyStyle } from "@guestsupply/App/Components/Notify";
import { createProductDescriptionStyles } from "@guestsupply/App/SharedStyles/productDescriptionStyles";
import xProductPartNumbersStyles from "@guestsupply/App/SharedStyles/ProductPartNumbersStyles";
import { ProductPriceStyles } from "@guestsupply/Overrides/Components/ProductPrice";
import FreightCharges from "@guestsupply/XDL/Icons/FreightCharges";
import Trash from "@guestsupply/XDL/Icons/Trash";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@guestsupply/XDL/Utilities";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Plus from "@insite/mobius/Icons/Plus";
import X from "@insite/mobius/Icons/X";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: IconPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    lineContainerStyles?: CartLineContainerStyles;
    surchargesTooltip?: TooltipPresentationProps;
    shippingWeightWrapper?: InjectableCss;
    totalFreightChargesLink?: LinkPresentationProps;
    headlineTypography?: TypographyProps;
    pricingFailedStyle?: TypographyProps;
    notify?: TypographyProps;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 91px;
                height: 91px;
                background-color: ${color("common.white")};
                justify-content: center;
                &&& > img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                }
            `,
        },
    },
    productDescription: {
        ...createProductDescriptionStyles(),
    },
    productPartNumbers: {
        container: {
            css: css`
                margin-bottom: 8px;
                flex-grow: 0;
                @media print {
                    display: inline;
                    float: left;
                }
            `,
        },
    },
    addToListLink: {
        ...createLinkIconStyles(Plus, {
            css: css`
                ${xScreen("lte", "md")} {
                    display: block;
                    margin-left: auto;
                }
            `,
            typographyProps: {
                size: 12,
            },
            icon: {
                iconProps: {
                    size: 15,
                    css: css`
                        margin-right: 7px;
                    `,
                },
                position: "left",
            },
        }),
    },
    productPrice: {
        price: {
            priceText: {
                size: 16,
            },
            realTimeText: {
                size: 16,
            },
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "13px",
                weight: "normal",
                css: css`
                    line-height: 13px;
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            // XNG-Changes: GSS-366
            text: {
                size: 16,
                weight: "bold",
                lineHeight: "17px",
                css: css`
                    text-align: start;
                    display: flex;
                    ${xScreen("lte", "md")} {
                        padding-top: 7px;
                    }
                `,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...xProductPartNumbersStyles.label,
    },
    costCodeValueText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionText: {
        ...xProductPartNumbersStyles.value,
    },
    configurationOptionLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...xProductPartNumbersStyles.label,
    },
    promotionNameText: {
        ...xProductPartNumbersStyles.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: "normal",
        lineHeight: "13px",
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
            @media print {
                &&& {
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },
    removeCartLineIcon: {
        src: Trash,
        size: 16,
        color: "primary",
        css: css`
            &:hover {
                color: ${color("common.ui8")};
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    productAvailability: {
        container: {
            css: css`
                margin-top: 4px;
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: "normal",
        css: css`
            line-height: 13px;
            text-transform: capitalize;
        `,
    },
    lineContainerStyles: {
        container: {
            css: css`
                &.group-cartline {
                    &:last-child {
                        border-bottom: 0;
                    }
                }
            `,
        },
    },
    shippingWeightWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            justify-content: flex-start;
            ${IconWrapper} {
                margin-left: 8px;
            }
            @media print {
                display: inline-block;
            }
        `,
    },
    surchargesTooltip: {
        cssOverrides: {
            tooltipClickable: css`
                margin-left: 0;
            `,
        },
    },

    totalFreightChargesLink: {
        ...createLinkIconStyles(FreightCharges, {
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    margin-left: 8px;
                    line-height: 12px;
                `,
            },
            icon: {
                iconProps: {
                    size: 12,
                    color: "text.link",
                },
                position: "left",
            },
            css: css`
                &&& {
                    display: flex;
                }
            `,
        }),
    },
    headlineTypography: {
        size: 12,
        lineHeight: "13px",
        css: css`
            display: none;
            margin-bottom: 5px;
            ${xScreen("lte", "md")} {
                display: block;
            }
        `,
    },
    pricingFailedStyle: {
        size: 16,
        weight: "bold",
        lineHeight: "17px",
        css: css`
            display: block;
        `,
    },
    notify: {
        css: css`
            margin-bottom: 0;
            margin-top: 8px;
        `,
    },
};

export default cartLineSharedStyles;
